import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingScreen } from './LoadingScreen';
import { Routes } from '@/routes/routeNames';

/**
 * sessionStorage key used to ferry the customer's intended destination
 * through the auth funnel. AuthGate writes it on bounce-to-login; the
 * terminal step of the funnel (OtpPage on existing-user auth, or
 * OnboardingPage Skip/Next for new users) reads + clears it and
 * navigates there.
 *
 * Threading via the URL would mean adding `?next=` to four route
 * helpers (login, otp, register, onboarding) and four pages. The
 * sessionStorage approach keeps the diff small and survives page
 * refreshes inside the funnel.
 */
export const POST_AUTH_REDIRECT_KEY = 'luvelle.postAuthRedirect';

export function consumePostAuthRedirect(fallback: string): string {
  try {
    const v = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
    return v || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Wrap protected routes with this. Redirects to /login while loading or
 * when the user is a guest. The pre-auth URL is preserved in
 * sessionStorage so the auth funnel can return the customer here once
 * they finish signing in.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { auth } = useAuth();
  const location = useLocation();
  if (auth.status === 'loading') return <LoadingScreen />;
  if (auth.status === 'guest') {
    try {
      sessionStorage.setItem(
        POST_AUTH_REDIRECT_KEY,
        location.pathname + location.search,
      );
    } catch {
      // sessionStorage can be disabled (private mode on some browsers);
      // worst case the customer lands on /home after auth instead of
      // back at the booking wizard — recoverable.
    }
    return <Navigate to={Routes.login.path} replace />;
  }
  return <>{children}</>;
}
